import ImageData from './ImageData'

export type ModuleType = 'SupportingColumn' | 'VariableModule' | 'LightingHead' | 'ImplicitModule'

type ModuleDataBase = {
  id: string
  /** Refers to a translation key */
  title: string
  /** Image that will be rendered inside the constela-preview */
  preview: ImageData
  /** Height in mm */
  height: number
  /** Width in mm */
  width: number
  /** Horizontal center position in mm. Default: width / 2 */
  center?: number
  /** Use {propertyName} to insert the value of system-/ or module-properties */
  triluxId?: string
  /** Dictionaries for additional "properties" to generate the triluxId */
  triluxIdParts?: {
    name: string
    property: string
    values: { [value: string]: string }
  }[]
}

type WithVariations<T extends { id: string }> = Omit<T, 'variations'> & {
  variations?: {
    matchAny: {
      [key: string]: string | string[]
    }[]
    override: Partial<Omit<T, 'variations' | 'id' | 'triluxId' | 'triluxIdParts' | 'type'>>
  }[]
}

export type SupportingColumnData = WithVariations<
  ModuleDataBase & {
    type: 'SupportingColumn'
  }
>
export const isSupportingColumnData = (module: ModuleData | undefined): module is SupportingColumnData => {
  return module?.type === 'SupportingColumn'
}

export type LinkInfo = {
  url: string
  label: string
}

export type VariableModuleData = WithVariations<
  ModuleDataBase & {
    type: 'VariableModule'
    /** Thumbnail to show on the button */
    thumbnail?: ImageData
    /** Thumbnail to show on the button on hover */
    thumbnailHover?: ImageData
    /** Hero-Image for the lightbox */
    heroImage?: ImageData
    /** Refers to a translation key */
    description: string
    /** Read-More Link */
    readMore?: LinkInfo
    /** Property-Overlay */
    propertyMenu?: {
      controlType: 'dropdown' | 'buttons'
      property: string
    }[]
  }
>

export type SingularModuleData = WithVariations<
  Omit<VariableModuleData, 'type'> & {
    type: 'SingularModule'
    /** Distance from the bottom of the luminary in mm */
    bottom: number
  }
>

export type ImplicitModuleData = WithVariations<
  ModuleDataBase & {
    type: 'ImplicitModule'
    /** Distance from the bottom of the luminary in mm */
    bottom: number
    /** When is this module added? */
    condition: {
      matchAny: {
        [key: string]: string | string[]
      }[]
    }
    /** Refers to a translation key */
    title?: string
  }
>

export type ServiceData = WithVariations<
  Omit<VariableModuleData, 'type' | 'width' | 'height'> & {
    type: 'Service'
    expertHidden?: boolean
    equivalentProperty?: {
      [key: string]: string
    }
  }
>
export const isServiceData = (module: ModuleData | undefined): module is ServiceData => {
  return module?.type === 'Service'
}

export type LightingHeadData = WithVariations<
  ModuleDataBase & {
    type: 'LightingHead'
    /** Thumbnail to show on the button */
    thumbnail?: ImageData
    /** Thumbnail to show on the button on hover */
    thumbnailHover?: ImageData
    /** Hero-Image for the lightbox */
    heroImage?: ImageData
    /** Refers to a translation key */
    description: string
    /** Read-More Link */
    readMore?: LinkInfo
  }
>
export const isLightingHeadData = (module: ModuleData | undefined): module is LightingHeadData => {
  return module?.type === 'LightingHead'
}

export type ModuleData =
  | SupportingColumnData
  | VariableModuleData
  | SingularModuleData
  | LightingHeadData
  | ServiceData
  | ImplicitModuleData
export type PlacableModuleData = SupportingColumnData | VariableModuleData | SingularModuleData | LightingHeadData

export type AvailableModuleData = Omit<
  Partial<
    Omit<SupportingColumnData, 'type'> &
      Omit<VariableModuleData, 'type'> &
      Omit<SingularModuleData, 'type'> &
      Omit<LightingHeadData, 'type'> &
      Omit<ServiceData, 'type'>
  >,
  'center' | 'width' | 'height' | 'id'
> & { id: string }

export type OverviewModuleData = VariableModuleData | SingularModuleData | ServiceData
export const isOverviewModuleData = (module: ModuleData | undefined): module is OverviewModuleData => {
  if (!module) {
    return false
  }
  return ['VariableModule', 'Service', 'SingularModule'].includes(module.type)
}

export const isSingularModule = (module: ModuleData | undefined): module is SingularModuleData => {
  return !!module && module.type === 'SingularModule'
}

export const isVariableModule = (module: ModuleData | undefined): module is VariableModuleData => {
  return !!module && module.type === 'VariableModule'
}

export const isPlacable = (module: ModuleData | undefined): module is PlacableModuleData => {
  return !!module && 'width' in module
}

export default ModuleData
